import { createFileRoute } from '@tanstack/react-router'

import { DayRailSection, SectionLabel, UrgentPanel } from '@/components/shared'
import { AppLink as Link } from '@/components/shell'
import { ShootPairRow, TaskCard } from '@/components/spravce/prehled'
import { MANAGER, QUEUE, SHOOT_DAYS, URGENT } from '@/mocks/spravce/prehled'

export const Route = createFileRoute('/spravce/prehled')({
  head: () => ({ meta: [{ title: 'Přehled · Aplikace Bitez' }] }),
  component: SpravcePrehled,
})

/**
 * Bára's morning, in the order she actually needs it: **the one thing that
 * cannot wait**, then what is physically happening today, then the rest of the
 * shift.
 *
 * The urgent panel is new here (2026-08-18) and it is the app's own rule
 * applied to her: every role gets exactly one brand-red panel, and for a
 * content specialist it is always the same shape — a batch nobody approved and
 * a clock that will publish it anyway (spec §7.9). Before this, that item was
 * one row among six identical ones in the queue.
 *
 * It states a fact rather than handing her a task: the client is already being
 * notified automatically, so "připomeňte jim to" would be work nobody should
 * do. She needs to know content is about to go out unapproved — and to open it
 * once more if she wants. Hers is also the **lower** variant of the panel: she
 * reads a ticking clock every day and does not need it shouted.
 *
 * The shoots moved onto the **day rail** the rest of the app uses for time. A
 * flat list with "dnes 10:00" chips made every row carry the day again; the
 * rail says it once, and "Dnes" at the top of a spine reads as a day rather
 * than as a filter.
 */
function SpravcePrehled() {
  return (
    <div className="px-4 pt-6 pb-6">
      {/* Her own avatar top-left is the door to the profile — the same pattern
          the client uses for the venue (no Profil tab in the bar). */}
      <header className="flex items-center gap-3.5">
        <Link href="/spravce/profil" aria-label="Váš profil" className="shrink-0">
          <img
            src={MANAGER.avatar}
            alt=""
            width={150}
            height={150}
            className="size-13 rounded-full object-cover shadow-sm ring-2 ring-white transition-transform duration-150 active:scale-95"
          />
        </Link>
        <div className="min-w-0">
          <SectionLabel>Středa 9. dubna</SectionLabel>
          <h1 className="font-display mt-0.5 text-2xl leading-[0.9] uppercase">
            Dobré ráno, {MANAGER.firstName}
          </h1>
        </div>
      </header>

      <div className="animate-rise-up mt-5">
        <UrgentPanel
          size="sm"
          href={URGENT.href}
          figure={URGENT.hoursLeft}
          icon="arrow-right"
          title="vyjde bez schválení"
          note={
            <>
              <span className="font-bold">{URGENT.venue}</span> · {URGENT.note}
            </>
          }
        />
      </div>

      <section className="animate-rise-up mt-7" style={{ animationDelay: '60ms' }}>
        <SectionLabel>Focení</SectionLabel>
        <div className="mt-4">
          {SHOOT_DAYS.map((group, groupIndex) => (
            <DayRailSection
              key={group.date}
              day={group.day}
              date={group.date}
              last={groupIndex === SHOOT_DAYS.length - 1}
            >
              <div className="space-y-3.5 pb-6">
                {group.shoots.map((shoot) => (
                  <ShootPairRow key={shoot.venue} shoot={shoot} />
                ))}
              </div>
            </DayRailSection>
          ))}
        </div>
      </section>

      {/* Everything else, by deadline. Each row opens the screen where the work
          happens — a queue whose items lead nowhere is a to-do list someone
          else still has to translate. */}
      <section className="animate-rise-up mt-4" style={{ animationDelay: '120ms' }}>
        <SectionLabel>Fronta</SectionLabel>
        <div className="mt-3 space-y-3.5">
          {QUEUE.map((task, index) => (
            <div
              key={`${task.venue}-${task.title}`}
              className="animate-rise-up"
              style={{ animationDelay: `${150 + index * 60}ms` }}
            >
              <TaskCard task={task} />
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
